import { startCase } from 'lodash';

import Workflow, { ActionStatus, WorkflowAction } from 'models/Workflow';

import { DEMOGRAPHIC_FIELDS, PORTAL_COLLECT_FIELDS } from '../constants';

import { ACTION_KINDS } from './constants';

/**
 * Why a task is on the list — the one derivation the card body, the type filter and the
 * slideout's intent all read. Derived from `(kind, effectiveStatus)` over the run's actions.
 */
export type TaskReason =
  | 'errored'
  | 'missingValues'
  | 'blocked'
  | 'login'
  | 'facilityMapping'
  | 'review'
  | 'stale'
  | 'unknown';

export const TASK_REASON_LABELS: Record<TaskReason, string> = {
  errored: 'Failed',
  missingValues: 'Missing information',
  blocked: 'Blocked',
  login: 'Portal login',
  facilityMapping: 'Facility mapping',
  review: 'Needs review',
  stale: 'Out of date',
  unknown: 'Other',
};

// Presentation reads effectiveStatus, behavior reads status — the codebase's own rule
// (WorkflowAction.ts). A card body is presentation, so a completed-but-flagged action must
// reach the ladder as flagged, the same way the referral inbox's pill sees it.
function withStatus(workflow: Workflow, status: ActionStatus): WorkflowAction[] {
  return workflow.actions.filter((action) => action.effectiveStatus === status);
}

/**
 * The status-aware ladder. Two things about its shape are deliberate and were established
 * against the engine rather than taken from the design's table:
 *
 *  1. `ExtractAttributes` ALWAYS completes, so a missing demographic never surfaces as a
 *     demographics action needing attention. It surfaces as `DisposePortalReferral` in
 *     `flagged`, carrying the missing names in `unobtainableValues` — which is what
 *     `missingValues` matches, and why there is no demographics rung.
 *
 *  2. The status tiers (errored, then flagged, then action_required) mirror
 *     `referralProgress.categoryStatus`, so a run's card body and its inbox pill can never
 *     disagree about which of its problems is the significant one. Kind priority applies only
 *     WITHIN a tier. The design's "retrieval first" is preserved inside the action_required
 *     tier, though it never actually contends: while `RetrievePortalInformation` is
 *     action_required it is still a live producer, so nothing downstream has flagged yet.
 *
 * `stale` is the fallthrough rather than a peer of `flagged` (where the inbox rollup puts it):
 * a run can be `action_required` purely because some completed action went stale, and that run
 * has no action in an attention status at all. Ranking it above real work would let one stale
 * input mask a facility a person actually has to map.
 */
export function taskReason(workflow: Workflow): TaskReason {
  if (withStatus(workflow, ActionStatus.ERRORED).length > 0) return 'errored';

  const flagged = withStatus(workflow, ActionStatus.FLAGGED);
  if (flagged.some((action) => action.kind === ACTION_KINDS.dispose)) return 'missingValues';
  if (flagged.length > 0) return 'blocked';

  const required = withStatus(workflow, ActionStatus.ACTION_REQUIRED);
  // `login` only while nothing has been captured. A retrieval that already carries staged
  // outputs (a sweep or an extension run parked them for confirmation) is a REVIEW moment —
  // telling that person to "log in and pull the details" describes work a machine already did.
  if (required.some((action) => action.kind === ACTION_KINDS.retrieve && action.stagedValueOutputs.length === 0))
    return 'login';
  if (required.some((action) => action.kind === ACTION_KINDS.mapFacilities)) return 'facilityMapping';
  if (required.length > 0) return 'review';

  if (workflow.actions.some((action) => action.stale)) return 'stale';

  return 'unknown';
}

// The action the card body is speaking about, so generic copy can name it. Same ladder, so the
// reason and the action it came from can never drift apart.
export function reasonAction(workflow: Workflow, reason: TaskReason): WorkflowAction | undefined {
  const first = (status: ActionStatus, kind?: string) =>
    withStatus(workflow, status).find((action) => !kind || action.kind === kind);

  switch (reason) {
    case 'errored':
      return first(ActionStatus.ERRORED);
    case 'missingValues':
      return first(ActionStatus.FLAGGED, ACTION_KINDS.dispose);
    case 'blocked':
      return first(ActionStatus.FLAGGED);
    case 'login':
      return first(ActionStatus.ACTION_REQUIRED, ACTION_KINDS.retrieve);
    case 'facilityMapping':
      return first(ActionStatus.ACTION_REQUIRED, ACTION_KINDS.mapFacilities);
    case 'review':
      return first(ActionStatus.ACTION_REQUIRED);
    case 'stale':
      return workflow.actions.find((action) => action.stale);
    default:
      return undefined;
  }
}

// Value name -> how it reads mid-sentence. Sourced from the field declarations this UI already
// owns rather than a second catalog; anything outside them (the engine has names no form
// renders, e.g. facility_group_ids) is humanized.
const VALUE_LABELS: Record<string, string> = {
  ...Object.fromEntries([...DEMOGRAPHIC_FIELDS, ...PORTAL_COLLECT_FIELDS].map((f) => [f.name, f.label.toLowerCase()])),
  facility_group_ids: 'a mapped facility',
  patient_first_name: 'first name',
  patient_last_name: 'last name',
};

function valueLabel(name: string): string {
  return VALUE_LABELS[name] ?? startCase(name).toLowerCase();
}

// "a", "a and b", "a, b and c" — one readable clause, not a bullet list, because the card body
// is one line.
function joinLabels(names: string[]): string {
  const labels = names.map(valueLabel);
  if (labels.length <= 1) return labels[0] ?? '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

export interface TaskCopyContext {
  // The portal's own name, resolved by the caller through usePortalName — this module holds no
  // catalog of its own.
  portalName: string;
}

/**
 * The card's one-line body. Pure: everything it needs is on the run plus the portal's name.
 */
export function taskCardBody(workflow: Workflow, { portalName }: TaskCopyContext): string {
  const reason = taskReason(workflow);
  const action = reasonAction(workflow, reason);
  const portal = portalName || 'the portal';

  switch (reason) {
    case 'errored':
      return `Automated retrieval from ${portal} failed — open this task to finish it by hand.`;
    case 'missingValues': {
      const missing = action?.unobtainableValues ?? [];
      // The design's line is the no-names case. When the engine did name what it couldn't
      // derive, saying so is strictly better copy — and it's the only case where the body can
      // be specific, because `unobtainableValues` is the only field-level detail on a list row.
      if (missing.length === 0) return 'Complete missing or incorrect patient demographics from portal scan';
      return `Add ${joinLabels(missing)} to finish this referral.`;
    }
    case 'blocked':
      return `${action?.displayLabel ?? 'A step'} is waiting on information Olio couldn’t find — open this task to provide it.`;
    case 'login':
      return `Log in to ${portal} and pull this referral’s details.`;
    case 'facilityMapping':
      return 'Map skilled facility from portal to Olio facility name';
    case 'review':
      // The captured-retrieval case: a sweep or extension run staged the details and the task
      // is the person confirming them, so the body says that rather than the action's name.
      if (action?.kind === ACTION_KINDS.retrieve)
        return `Review the details pulled from ${portal} and complete anything that’s missing.`;
      return `${action?.displayLabel ?? 'This referral'} needs your review.`;
    case 'stale':
      return 'Information on this referral changed after it ran — open this task to re-run the affected steps.';
    default:
      // Never blank. A run reaches the list because the server put it in `action_required`, so
      // a body we can't derive is a gap in this derivation, not an absence of work.
      return 'This referral needs your attention.';
  }
}
